import getClassNames from 'classnames';
import contains from 'lodash/fp/contains';
import upperFirst from 'lodash/fp/upperFirst';
import { createElement } from 'react';
import { ClassArray, ClassValue } from 'classnames/types';
import getResponsiveClasses, {
  ResponsiveSelector,
} from '../../shared/utils/getResponsiveClasses/getResponsiveClasses';
import flexStyles from '../Flex/Flex.module.scss';
import colors from './Colors.module.scss';
import styles from './Element.module.scss';
import getBorderRadiusClasses, {
  BorderRadiusProps,
} from './getBorderRadiusClasses';
import getMarginClasses, { MarginProps } from './getMarginClasses';
import getPaddingClasses, { PaddingProps } from './getPaddingClasses';
import hiddenStyles from './Hidden.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';

type TagName = keyof JSX.IntrinsicElements;

export type CommonProps<T extends TagName> = Omit<ElementProps<T>, 'tagName'>;

export type ElementProps<T extends TagName> = {
  /** Element class name */
  className?: ClassValue;
  /** HTML tag name: div, span, h1 etc */
  tagName: T;
  /** Margin for the element */
  margin?: MarginProps | false | null;
  /** Padding for the element */
  padding?: PaddingProps;
  /** Color of the text inside the element */
  color?: SupportedColor;
  /** Element hover color */
  hoverColor?: SupportedColor;
  /** Background color of the element */
  backgroundColor?: SupportedColor;
  /** Element background hover color */
  backgroundHoverColor?: SupportedColor;
  /** Set element to be an inline block element */
  inlineBlock?: boolean;
  /** Set element to be a block element */
  block?: boolean;
  /** Show element */
  shown?: boolean;
  /** Hide element */
  hidden?: boolean;
  /** Set element to be a flex item */
  flexItem?: FlexItemProp;
  /** Add different box shadow to element */
  boxShadow?: SupportedBoxShadow;
  /** Element hover box shadow */
  hoverBoxShadow?: SupportedBoxShadow;
  /** Hide element in specific breakpoints */
  responsiveHidden?: ResponsiveHiddenProp;
  /** Occupy vertical space, used with Flex */
  occupyVerticalSpace?: boolean;
  /** Occupy horizontal space, used with Flex */
  occupyHorizontalSpace?: boolean;
  /** Force wrap for element text */
  forceWrap?: boolean;
  /** React ref */
  refElement?: React.Ref<any>;
  /** Stop content from overflowing outside of element */
  hideOverflow?: boolean;
  /** Allow content to overflow outside of element */
  showOverflow?: boolean;
  /** Align text to center */
  alignTextToCenter?: boolean;
  /** Align text to right */
  alignTextToRight?: boolean;
  /** Sets "cursor: pointer" style for the element to indicate the clickability. Is encouraged to use together with hoverColor and backgroundHoverColor props. */
  clickableAppearance?: boolean;
  /** Border radius for element */
  borderRadius?: BorderRadiusProps;
  /** Allow passing fontweight of normal, medium, bold */
  fontWeight?: SupportedFontWeight;
} & Omit<JSX.IntrinsicElements[T], 'color' | 'className' | 'ref' | 'hidden'>;

export default function Element<T extends TagName>({
  className,
  tagName,
  children,
  margin,
  padding,
  color,
  hoverColor,
  backgroundColor,
  backgroundHoverColor,
  inlineBlock,
  block,
  shown,
  hidden = shown === undefined ? undefined : !shown,
  flexItem,
  boxShadow,
  hoverBoxShadow,
  responsiveHidden,
  occupyVerticalSpace,
  occupyHorizontalSpace,
  forceWrap,
  style: explicitStyles = {},
  refElement,
  hideOverflow,
  showOverflow,
  alignTextToCenter,
  alignTextToRight,
  clickableAppearance,
  borderRadius,
  fontWeight,
  ...props
}: ElementProps<T>) {
  const classNameParameters: ClassArray = [styles.element];

  if (margin === false || margin === null) margin = { size: 'zero' };
  if (margin) classNameParameters.push(...getMarginClasses(margin));
  if (padding) classNameParameters.push(...getPaddingClasses(padding));
  if (borderRadius)
    classNameParameters.push(getBorderRadiusClasses(borderRadius));
  if (color) classNameParameters.push(colors[`color__${color}`]);
  if (hoverColor)
    classNameParameters.push(colors[`color__${hoverColor}Hoverable`]);
  if (block) classNameParameters.push(styles.element__block);
  if (inlineBlock) classNameParameters.push(styles.element__inlineBlock);
  if (backgroundColor)
    classNameParameters.push(colors[`color_background__${backgroundColor}`]);
  if (backgroundHoverColor)
    classNameParameters.push(
      colors[`color_background__${backgroundHoverColor}Hoverable`]
    );
  if (className) classNameParameters.push(className);
  if (hidden !== undefined)
    classNameParameters.push({
      [styles.element__hidden]: hidden,
      'hidden-e2e-test': hidden,
      'shown-e2e-test': !hidden,
    });
  if (flexItem) classNameParameters.push(flexStyles.flex_flexItem);
  if (boxShadow)
    classNameParameters.push(styles[`element__boxShadow__${boxShadow}`]);
  if (hoverBoxShadow)
    classNameParameters.push(
      styles[`element_boxShadowHoverable__${hoverBoxShadow}`]
    );
  if (alignTextToCenter)
    classNameParameters.push(styles.element__alignTextToCenter);
  if (alignTextToRight)
    classNameParameters.push(styles.element__alignTextToRight);

  if (responsiveHidden) {
    classNameParameters.push(
      ...getResponsiveClasses({
        styles: hiddenStyles,
        prefix: 'hidden__',
        selector: responsiveHidden,
      })
    );
  }

  if (occupyVerticalSpace)
    classNameParameters.push(styles.element__occupyVerticalSpace);

  if (occupyHorizontalSpace)
    classNameParameters.push(styles.element__occupyHorizontalSpace);

  if (forceWrap) classNameParameters.push(styles.element__forceWrap);
  if (hideOverflow) classNameParameters.push(styles.element__hideOverflow);
  if (showOverflow) classNameParameters.push(styles.element__showOverflow);

  if (clickableAppearance)
    classNameParameters.push(styles.element__clickableAppearance);

  if (flexItem && flexItem !== true && flexItem.alignSelfTo) {
    classNameParameters.push(
      styles[`element__alignSelfTo${upperFirst(flexItem.alignSelfTo)}`]
    );
  }

  if (fontWeight) {
    classNameParameters.push(styles[`element__fontWeight__${fontWeight}`]);
  }

  const classNames = getClassNames(classNameParameters);

  if (contains('undefined', classNames)) {
    throw new Error(
      `Undefined class name encountered in Element "${tagName}": "${classNames}"`
    );
  }

  const style: React.CSSProperties = {
    ...(flexItem &&
      flexItem !== true &&
      flexItem.sizeMultiplier && { flexGrow: flexItem.sizeMultiplier }),
    ...explicitStyles,
  };

  return createElement(
    tagName,
    {
      className: classNames,
      ref: refElement,
      ...(Object.keys(style).length > 0 && { style }),
      ...props,
    },
    children
  );
}

export const supportedColors = [
  'inherit',
  'transparent',
  'overlay',
  'white',
  'black',
  'purple100',
  'purple200',
  'purple300',
  'purple400',
  'purple500',
  'purple600',
  'purple700',
  'purple800',
  'purple900',
  'gray100',
  'gray200',
  'gray300',
  'gray400',
  'gray500',
  'gray600',
  'gray700',
  'gray800',
  'gray900',
  'red100',
  'red200',
  'red300',
  'red400',
  'red500',
  'red600',
  'red700',
  'red800',
  'red900',
  'blue100',
  'blue200',
  'blue300',
  'blue400',
  'blue500',
  'blue600',
  'blue700',
  'blue800',
  'blue900',
  'orange100',
  'orange200',
  'orange300',
  'orange400',
  'orange500',
  'orange600',
  'orange700',
  'orange800',
  'orange900',
  'green100',
  'green200',
  'green300',
  'green400',
  'green500',
  'green600',
  'green700',
  'green800',
  'green900',
] as const;

export type SupportedColor = ArrayElementType<typeof supportedColors>;

export const supportedFontWeights = ['normal', 'medium', 'bold'] as const;

export type SupportedFontWeight = ArrayElementType<typeof supportedFontWeights>;

export const supportedSizes = [
  'zero',
  'xxxs',
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xlg',
  'xxlg',
] as const;

export type SupportedSize = ArrayElementType<typeof supportedSizes>;

export const responsiveSupportedSizes = [
  'xxxs',
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xlg',
] as const;

export type ResponsiveSupportedSize = ArrayElementType<
  typeof responsiveSupportedSizes
>;

export type SupportedBoxShadow = 'zero' | 'sm' | 'md' | 'lg';

export type ResponsiveHiddenProp = false | ResponsiveSelector;

// Aliases for backwards compatibility
export type Color = SupportedColor;
export type ElementAttributes = ElementProps<TagName>;
export type FontWeights = SupportedFontWeight;
export type ShadowSize = SupportedBoxShadow;
export type SpacingSize = SupportedSize;

export type FlexItemProp =
  | boolean
  | { sizeMultiplier?: number; alignSelfTo?: 'bottom' };
